import { Component, Vue, PropSync, Watch } from 'vue-property-decorator';
import { ConfigurationSection } from '@/models/configuration/Section';
import { ConfigurationSectionTypeEnum } from '@/enums/ConfigurationSectionTypeEnums';
import { ConfigurationField, IConfigurationField } from '@/models/configuration/Field';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { Helpers } from '@/helpers/Helpers';
import { IFieldEvent } from '../field/Field';
import { FieldEventTypeEnums } from '@/enums/FieldEventTypeEnums';
import { ApiIdentifers } from '@/config/index';
import { LoggerService } from '@/utils';
import { ConfigurationService, QuickEditService } from '@/services';

import { ISectionFieldsComponentMethods } from './sectionfields/SectionFields';
import SectionFields from './sectionfields/SectionFields.vue';
import SectionFieldsTable from './sectionfieldstable/SectionFieldsTable.vue';
import Field from '@/components/edit/field/Field.vue';



export interface ISectionComponentMethods {
    validateSection(): boolean;
}

@Component({
    components: {
        Field,
        SectionFields,
        SectionFieldsTable,
    },
})
export default class SectionComponent extends Vue implements ISectionComponentMethods {
    @PropSync('name', { type: String }) public syncedName!: string;
    @PropSync('sectionId', { type: Number }) public syncedSectionId!: number;
    /**
     * Use table mode for editing.
     * default: true
     */
    @PropSync('table', { type: Boolean, default: true }) public syncedTable!: boolean;
    @PropSync('quickEdit', { type: Boolean, default: false }) public syncedQuickEdit!: boolean;
    @PropSync('openAll', { type: Boolean, default: false }) public syncedOpenAll!: boolean;
    @PropSync('previewReadonly', { type: Boolean, default: false }) public previewReadonlyValue!: boolean;

    public sectionConfiguration: ConfigurationSection = new ConfigurationSection();
    public configurationRoom: ConfigurationSection = new ConfigurationSection();

    public mounted() {
        LoggerService.LogDebug(`mounted section: ${this.syncedSectionId}`);

        if (this.syncedQuickEdit === false) {
            this.sectionConfiguration = this.getUsedSectionConfiguration;
        } else {
            this.sectionConfiguration = this.getUsedQuickEditSectionConfiguration;
        }

        if (this.sectionConfiguration.type === ConfigurationSectionTypeEnum.MACHINE_ROOM) {
            this.sortSectionsFields(this.sectionConfiguration.fields);
        } else if (this.sectionConfiguration.identifier === ApiIdentifers.sample) {
            this.filterExtractionPoints(this.sectionConfiguration);
        } else if (this.sectionConfiguration.identifier === ApiIdentifers.customer) {
            this.addSerialnumberSearch(this.sectionConfiguration);
        }

    }

    get getUsedSectionConfiguration() {
        return ConfigurationService.usedConfigurationSections[this.syncedSectionId];
    }

    get getUsedQuickEditSectionConfiguration() {
        return QuickEditService.usedQuickEditConfigurationSections[this.syncedSectionId];
    }

    get getConfigurationRoom() {
        return ConfigurationService.machinesConfiguration.sections.find((room) => room.identifier === this.sectionConfiguration.identifier);
    }

    public onSubSectionClick(index: number) {
        LoggerService.LogDebug(`SubSection ${index} clicked`);
    }

    // @Watch('sectionConfiguration', { deep: true })
    // public onSectionConfigurationChanged(newValue: ConfigurationSection) {
    //     // LoggerService.LogDebug(`onSectionConfigurationChanged (${this.sectionConfiguration.name}): `, newValue);
    //     // ConfigurationService.SetUsedConfigurationSection(newValue);
    // }

    public getAdditionalClass(): string {
        let addclass = '';
        // may think about an enum of all available section names?
        if (this.sectionConfiguration.identifier === ApiIdentifers.machineOrder) {
            addclass += ' machine-order';
        }
        return addclass;
    }

    public validateSection(): boolean {
        const sectionRef = this.getSectionFieldsRef(this.getRefName(this.sectionConfiguration.name));
        return sectionRef.validateSectionFields();
    }

    public checkCheckAll() {
        if (this.sectionConfiguration.identifier === ApiIdentifers.machineOrder && this.sectionConfiguration.fields[0].valueBool === true) {
            const someUnchecked = this.sectionConfiguration.fields.some((field) => field.valueBool === undefined || field.valueBool === false);
            if (someUnchecked === true) {
                this.sectionConfiguration.fields[0].valueBool = false;
                if (this.syncedQuickEdit === false) {
                    ConfigurationService.SetUsedConfigurationSection(this.sectionConfiguration);
                } else {
                    QuickEditService.SetUsedQuickEditConfigurationSection(this.sectionConfiguration);
                }
            }
        }
    }

    public onFieldEvent(event: IFieldEvent) {
        // may think about an enum of all available section names?
        if (this.sectionConfiguration.identifier === ApiIdentifers.machineOrder) {

            if (event.type === FieldEventTypeEnums.CHECKBOX_ALL) {
                this.setCheckAll(event.value as boolean);
            } else if (event.type === FieldEventTypeEnums.CHECKBOX) {
                this.checkCheckAll();
            }

        } else if (event.type === FieldEventTypeEnums.CHECK_UNIT_FIELD) {
            let unitField: ConfigurationField | undefined;

            for (const sectionField of this.sectionConfiguration.fields) {
                unitField = this.findSectionField(sectionField, event.value);

                if (event.actionIndex && parseInt(sectionField.identifier.split('__')[1] || '-1', 10) !== event.actionIndex) {
                    /**
                     * If we have an actions ection, e.g. material, we need to chekc the correct action-section;
                     */
                    continue;
                }

                if (unitField !== undefined) {
                    let unitFormatField: ConfigurationField | undefined;
                    for (const sectionFieldUnit of this.sectionConfiguration.fields) {
                        if (event.actionIndex && parseInt(sectionFieldUnit.identifier.split('__')[1] || '-1', 10) !== event.actionIndex) {
                            /**
                             * If we have an actions ection, e.g. material, we need to chekc the correct action-section;
                             */
                            continue;
                        }

                        unitFormatField = this.findSectionField(sectionFieldUnit, unitField.unit || '');
                        if (unitFormatField !== undefined) {
                            if (unitField.valueNumber !== null && unitField.valueNumber !== undefined && !isNaN(unitField.valueNumber)) {
                                unitFormatField.required = true;
                            } else {
                                unitFormatField.required = false;
                            }
                            break;
                        }
                    }
                    break;
                }
            }
        }
    }

    public getRefName(name: string): string {
        return Helpers.getReferenceName(name, 'sf');
    }

    public showTable() {
        return this.syncedTable && this.sectionConfiguration.identifier !== ApiIdentifers.machineOrder;
    }

    private setCheckAll(isChecked: boolean) {
        if (this.sectionConfiguration.identifier === ApiIdentifers.machineOrder && this.sectionConfiguration.fields) {
            if (isChecked === true) {
                // index 0 is "ALL" so we dont need to set it here
                for (let index = 1; index < this.sectionConfiguration.fields.length; index++) {
                    this.sectionConfiguration.fields[index].valueBool = isChecked;
                }
            } else {
                let sections: ConfigurationSection[] = [];
                if (this.syncedQuickEdit === false) {
                    sections = ConfigurationService.usedConfigurationSections;
                } else {
                    sections = QuickEditService.usedQuickEditConfigurationSections;
                }
                for (let index = 1; index < this.sectionConfiguration.fields.length; index++) {
                    this.sectionConfiguration.fields[index].valueBool =
                        sections.some((sec) => sec.identifier === this.sectionConfiguration.fields[index].identifier);
                }
            }

            // ConfigurationService.SetUsedConfigurationSection(this.sectionConfiguration);
        }
    }

    private sortSectionsFields(sectionFields: ConfigurationField[]) {
        sectionFields.forEach((sectionField: ConfigurationField) => {
            if (sectionField.type === FieldTypeEnum.SUBFIELD && sectionField.fields) {
                LoggerService.LogDebug(`sortSectionsFields for subfields: ${sectionField.name}`);
                this.sortSectionsFields(sectionField.fields);
            }
        });

        sectionFields.sort(this.sortFields);
    }

    private sortFields(lhs: ConfigurationField, rhs: ConfigurationField): number {
        if ((lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD)
            && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 0;
        } else if ((lhs.type !== FieldTypeEnum.SUBFIELD && lhs.type !== FieldTypeEnum.ACTION_SUBFIELD)
            && (rhs.type === FieldTypeEnum.SUBFIELD || rhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return -1;
        } else if ((rhs.type !== FieldTypeEnum.SUBFIELD && rhs.type !== FieldTypeEnum.ACTION_SUBFIELD)
            && (lhs.type === FieldTypeEnum.SUBFIELD || lhs.type === FieldTypeEnum.ACTION_SUBFIELD)) {
            return 1;
        }
        return 1;
    }

    private getSectionFieldsRef(sectionRefName: string): ISectionFieldsComponentMethods & SectionFields {
        return Helpers.getReference<ISectionFieldsComponentMethods & SectionFields>(this.$refs, sectionRefName);
    }

    /**
     * Method to disabled the already used extratcion points
     * @param inSampleSection
     */
    private filterExtractionPoints(inSampleSection: ConfigurationSection) {
        const usedExPoints: number[] = [];
        // first get all already used extration points
        inSampleSection.fields.forEach((sample) => {
            if (sample.fields) {
                const lab = sample.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
                if (lab && lab.fields) {
                    const exPoints = lab.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                    if (exPoints && exPoints.valueNumber != null) {
                        usedExPoints.push(exPoints.valueNumber);
                    }
                }
            }
        });

        /**
         * disable all used extration points for each added sample,
         * but exclude the extraction point for the actual section
         */
        inSampleSection.fields.forEach((sample) => {
            if (sample.fields) {
                const lab = sample.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratory);
                if (lab && lab.fields) {
                    const exPoint = lab.fields.find((f) => f.identifier === ApiIdentifers.sampleLaboratoryExtractionPoint);
                    if (exPoint && exPoint.values && exPoint.valueNumber != null) {
                        exPoint.values.forEach((exp) => {
                            if (exp.value !== exPoint.valueNumber
                                && usedExPoints.some((x) => x === exp.value)) {
                                exp.disabled = true;
                            } else {
                                exp.disabled = false;
                            }
                        });
                    }
                }
            }
        });
    }

    private findSectionField(inField: ConfigurationField, inIdentififer: string): ConfigurationField | undefined {
        let searchField: ConfigurationField | undefined;
        if (inField.identifier === inIdentififer) {
            searchField = inField;
        } else if (inField.fields !== undefined) {
            for (const subField of inField.fields) {
                const subSearchField = this.findSectionField(subField, inIdentififer);
                if (subSearchField !== undefined) {
                    searchField = subSearchField;
                    break;
                }
            }
        }
        return searchField;
    }

    private addSerialnumberSearch(inCustomerSection: ConfigurationSection) {
        /**
         * Add an additional field in company sub section to filter
         * company by a machine serial number.
         * TODO: check if it maybe is possible to add this as a new field type in
         * config files later?
         */
        const company = inCustomerSection.fields.find((f) => f.identifier === ApiIdentifers.customerCompany);
        if (company && company.fields) {
            const searchField = new ConfigurationField();
            searchField.identifier = 'ef58da8f';
            searchField.collapsible = false;
            searchField.isValid = true;
            searchField.name = 'Fitler by Identification Number';
            searchField.numericType = 2;
            searchField.position = 0;
            searchField.required = false;
            searchField.type = FieldTypeEnum.TEXT;
            company.fields.unshift(searchField);
        }
    }
}
